import { extractUpdatedAt } from "../utils/extractUpdatedAt";
import { useCallback, useEffect, useMemo, useState } from "react";
import { db } from "~/db/db";
import { useGetVloz, useGetPassouGanhou } from "~hooks/useSincronizacao";
import { vlozMock } from "../mocks/new/Vloz.mock";
import { passouGanhouMock } from "../mocks/new/PassouGanhou.mock";
import useVendedor from "~fetchs/useVendedor";
import { formatMes, formatMesLong, fmtClientes, fmtCurrency } from "../utils/formatters";
import { usarMock } from "../utils/dataSource";

const hasMock = usarMock(vlozMock);

const sumFields = (objs, keys) =>
  Object.fromEntries(keys.map(k => [k, objs.reduce((acc, o) => acc + (o?.[k] ?? 0), 0)]));

const mergeByInicioMes = (items, fields) => {
  const map = new Map();
  for (const item of items) {
    if (!map.has(item.inicioMes)) {
      map.set(item.inicioMes, { inicioMes: item.inicioMes, ...Object.fromEntries(fields.map(f => [f, 0])) });
    }
    const entry = map.get(item.inicioMes);
    for (const f of fields) entry[f] += item[f] ?? 0;
  }
  return Array.from(map.values());
};

const mergeTopItems = (items, groupKey, sumKey) => {
  const map = new Map();
  for (const item of items) {
    if (!map.has(item[groupKey])) map.set(item[groupKey], { ...item, [sumKey]: 0 });
    map.get(item[groupKey])[sumKey] += item[sumKey] ?? 0;
  }
  return Array.from(map.values()).sort((a, b) => b[sumKey] - a[sumKey]);
};

const sortByMes  = (arr) => [...arr].sort((a, b) => new Date(a.inicioMes) - new Date(b.inicioMes));
const filtrarAte = (arr, corteIso) => (corteIso ? arr.filter((e) => e.inicioMes <= corteIso) : arr);

const vlozCardFields = ['clientesCadastrados', 'novosClientes', 'tpv', 'split'];

const flattenVendedorVloz = (rawArr, vendedorSelecionado) => {
  if (!Array.isArray(rawArr) || !rawArr.length) return rawArr ?? [];
  if (!rawArr.some(v => Array.isArray(v?.basesVloz))) return rawArr;

  const filtered = vendedorSelecionado !== null
    ? rawArr.filter(v => v?.id === vendedorSelecionado)
    : rawArr;

  const all = filtered.flatMap(v => v?.basesVloz ?? []).filter(i => !i.createdAt);
  if (filtered.length <= 1) return all;

  const allCards    = all.filter(i => i.cards          != null).map(i => i.cards);
  const allCardsMes = all.filter(i => i.cardsMesPassado != null).map(i => i.cardsMesPassado);

  return [
    allCards.length    ? { cards:           sumFields(allCards,    vlozCardFields) } : null,
    allCardsMes.length ? { cardsMesPassado:  sumFields(allCardsMes, vlozCardFields) } : null,
    { clientesTransVloz:  mergeByInicioMes(all.flatMap(i => i.clientesTransVloz  ?? []), ['clientes']) },
    { novosCadastrosVloz: mergeByInicioMes(all.flatMap(i => i.novosCadastrosVloz ?? []), ['clientes']) },
  ].filter(Boolean);
};

const passouGanhouMonthFields = ['naoTransacionaram', 'bateramMeta', 'transacionouNaoBateu'];
const passouGanhouCardFields  = ['clientesCampanha', 'clientesBateramMeta', 'tpvTotalCampanha', 'beneficioPeriodo'];

const flattenVendedorPassouGanhou = (rawArr, vendedorSelecionado) => {
  if (!Array.isArray(rawArr) || !rawArr.length) return rawArr ?? [];
  if (!rawArr.some(v => Array.isArray(v?.basesPassouGanhou))) return rawArr;

  const filtered = vendedorSelecionado !== null
    ? rawArr.filter(v => v?.id === vendedorSelecionado)
    : rawArr;

  const all = filtered.flatMap(v => v?.basesPassouGanhou ?? []).filter(i => i.id !== 7);
  if (filtered.length <= 1) return all;

  const byId = new Map();
  for (const item of all) {
    if (!byId.has(item.id)) byId.set(item.id, []);
    byId.get(item.id).push(item);
  }

  const getById = (id) => byId.get(id) ?? [];
  const sumCard  = (groups, cardKey) => sumFields(groups.map(i => i?.[cardKey] ?? {}), passouGanhouCardFields);

  return [
    getById(1).length ? { id: 1, campanhaPassouGanhous:    mergeByInicioMes(getById(1).flatMap(i => i.campanhaPassouGanhous    ?? []), passouGanhouMonthFields) } : null,
    getById(2).length ? { id: 2, campanhaPassouGanhous3x:  mergeByInicioMes(getById(2).flatMap(i => i.campanhaPassouGanhous3x  ?? []), passouGanhouMonthFields) } : null,
    getById(3).length ? { id: 3, cardPassouGanhou:          sumCard(getById(3), 'cardPassouGanhou')  } : null,
    getById(4).length ? { id: 4, cardPassouGanhou3x:        sumCard(getById(4), 'cardPassouGanhou3x') } : null,
    getById(5).length ? { id: 5, topBandeirasCashback:       mergeTopItems(getById(5).flatMap(i => i.topBandeirasCashback       ?? []), 'bandeira', 'transacao') } : null,
    getById(6).length ? { id: 6, topClientesTransacaoVlozDto: mergeTopItems(getById(6).flatMap(i => i.topClientesTransacaoVlozDto ?? []), 'codigo',   'transacao') } : null,
  ].filter(Boolean);
};

const extractItems = (source) => {
  if (Array.isArray(source?.[0]?.[0]?.data)) {
    return source[0][0]?.data?.[0] ?? [];
  }
  const arr =
    source?.data?.data?.[0] ??
    source?.data?.[0] ??
    source?.[0] ??
    null;
  return Array.isArray(arr) ? arr : [];
};

const buildCards = (c) => [
  { title: "Clientes Cadastrados", value: fmtClientes(c.clientesCadastrados), color: "navy"  },
  { title: "Novos Clientes:",      value: fmtClientes(c.novosClientes),       color: "green" },
  { title: "TPV",                  value: fmtCurrency(c.tpv),                 color: "amber" },
  { title: "SPLIT",                value: fmtCurrency(c.split),               color: "blue"  },
];

const buildDadosCampanha = (card, sorted) => ({
  statCards: [
    { label: "Clientes em Campanha",      value: fmtClientes(card.clientesCampanha)    },
    { label: "Clientes que Bateram Meta", value: fmtClientes(card.clientesBateramMeta) },
    { label: "TPV Total da Campanha",     value: fmtCurrency(card.tpvTotalCampanha)    },
    { label: "Benefício no período:",     value: fmtCurrency(card.beneficioPeriodo)     },
  ],
  labels:               sorted.map((e) => formatMes(e.inicioMes)),
  naoTransacionaram:    sorted.map((e) => e.naoTransacionaram),
  bateramMeta:          sorted.map((e) => e.bateramMeta),
  transacionouNaoBateu: sorted.map((e) => e.transacionouNaoBateu),
});

const computeVloz = (vloz, passou) => {
  const cardsAtual      = vloz.find((i) => i.cards)?.cards ?? {};
  const cardsAnterior   = vloz.find((i) => i.cardsMesPassado)?.cardsMesPassado ?? {};
  const transVlozSorted = sortByMes(vloz.find((i) => i.clientesTransVloz)?.clientesTransVloz ?? []);
  const novosCadSorted  = sortByMes(vloz.find((i) => i.novosCadastrosVloz)?.novosCadastrosVloz ?? []);

  const passouById = new Map(passou.map((i) => [i.id, i]));

  const periodoAtual    = transVlozSorted.at(-1)?.inicioMes;
  const periodoAnterior = transVlozSorted.at(-2)?.inicioMes;

  const vlozSections = [
    {
      id: "vloz",
      title: "VLOZ",
      actions: [
        { key: "mes-atual",    label: "Mês Atual",    active: true,  period: periodoAtual    ? formatMesLong(periodoAtual)    : undefined, cards: buildCards(cardsAtual)    },
        { key: "mes-anterior", label: "Mês Anterior", active: false, period: periodoAnterior ? formatMesLong(periodoAnterior) : undefined, cards: buildCards(cardsAnterior) },
      ],
    },
  ];

  const dadosCampanha = buildDadosCampanha(
    passouById.get(3)?.cardPassouGanhou  ?? {},
    sortByMes(passouById.get(1)?.campanhaPassouGanhous ?? []),
  );

  const dadosCampanha3x = buildDadosCampanha(
    passouById.get(4)?.cardPassouGanhou3x ?? {},
    sortByMes(passouById.get(2)?.campanhaPassouGanhous3x ?? []),
  );

  const itensTopClientes  = (passouById.get(6)?.topClientesTransacaoVlozDto ?? []).slice(0, 5).map((e) => ({ nome: e.razaoSocial, valor: e.transacao }));
  const itensTopBandeiras = (passouById.get(5)?.topBandeirasCashback        ?? []).slice(0, 5).map((e) => ({ nome: e.bandeira,    valor: e.transacao }));

  return {
    vlozSections,
    transVlozSorted,
    novosCadSorted,
    periodoAtual,
    periodoAnterior,
    dadosCampanha,
    dadosCampanha3x,
    itensTopClientes,
    itensTopBandeiras,
  };
};

const syncToDb = (table, payload) =>
  table.clear().then(() => table.add({ payload, syncedAt: new Date().toISOString() }));

export const useVlozData = () => {
  const { vendedorSelecionado } = useVendedor();
  const [vlozItems,   setVlozItems]   = useState(null);
  const [passouItems, setPassouItems] = useState(null);
  const [isLoading,    setIsLoading]    = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const { data: vlozData, isFetching: fetchingVloz, isRefetching: refetchingVloz } =
    useGetVloz(!hasMock && navigator.onLine, 0, 0, null);

  const { data: passouData, isFetching: fetchingPassou, isRefetching: refetchingPassou } =
    useGetPassouGanhou(!hasMock && navigator.onLine, 0, 0, null);

  const loadFromDexie = useCallback(async () => {
    try {
      const [vlozRows, passouRows] = await Promise.all([
        db.vloz.toArray(),
        db.passouGanhou.toArray(),
      ]);
      if (vlozRows?.length)   setVlozItems(vlozRows[0]?.payload ?? null);
      if (passouRows?.length) setPassouItems(passouRows[0]?.payload ?? null);
    } catch (err) {
      console.error('Erro ao carregar vloz do Dexie:', err);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (hasMock) {
      setVlozItems(extractItems(vlozMock));
      setPassouItems(extractItems(passouGanhouMock));
      setIsLoading(false);
      return;
    }
    loadFromDexie();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (hasMock) return;
    window.addEventListener('online', loadFromDexie);
    return () => window.removeEventListener('online', loadFromDexie);
  }, [loadFromDexie]);

  useEffect(() => {
    if (hasMock || !navigator.onLine) return;

    const isFetching = fetchingVloz || refetchingVloz || fetchingPassou || refetchingPassou;
    if (isFetching) {
      setIsRefreshing(true);
      return;
    }

    if (!vlozData && !passouData) return;

    const nextVloz   = vlozData   ? extractItems(vlozData)   : vlozItems;
    const nextPassou = passouData ? extractItems(passouData) : passouItems;

    if (nextVloz)   { setVlozItems(nextVloz);     syncToDb(db.vloz,         nextVloz);   }
    if (nextPassou) { setPassouItems(nextPassou);  syncToDb(db.passouGanhou, nextPassou); }

    setIsRefreshing(false);
    setIsLoading(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchingVloz, refetchingVloz, fetchingPassou, refetchingPassou, vlozData, passouData]);

  const normalizedVloz   = useMemo(() => flattenVendedorVloz(vlozItems   ?? [], vendedorSelecionado), [vlozItems,   vendedorSelecionado]);
  const normalizedPassou = useMemo(() => flattenVendedorPassouGanhou(passouItems ?? [], vendedorSelecionado), [passouItems, vendedorSelecionado]);
  const computed         = useMemo(() => computeVloz(normalizedVloz, normalizedPassou),               [normalizedVloz, normalizedPassou]);

  const updatedAt = useMemo(() => extractUpdatedAt([vlozItems, passouItems]), [vlozItems, passouItems]);

  return { ...computed, filtrarAte, updatedAt, isLoading, isRefreshing };
};
